const PIX_KEY_TYPES = ["cpf", "cnpj", "phone", "email", "random"];
const PIX_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PIX_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function onlyDigits(value) {
  return String(value ?? "").replace(/\D/g, "");
}

function weightedMod11(digits, weights) {
  const sum = digits.reduce((total, digit, index) => total + digit * weights[index], 0);
  const rest = sum % 11;
  return rest < 2 ? 0 : 11 - rest;
}

export function isValidCpf(value) {
  const digits = onlyDigits(value);
  if (digits.length !== 11 || /^(\d)\1+$/.test(digits)) return false;
  const nums = digits.split("").map(Number);
  const d1 = weightedMod11(nums.slice(0, 9), [10, 9, 8, 7, 6, 5, 4, 3, 2]);
  const d2 = weightedMod11(nums.slice(0, 10), [11, 10, 9, 8, 7, 6, 5, 4, 3, 2]);
  return nums[9] === d1 && nums[10] === d2;
}

export function isValidCnpj(value) {
  const digits = onlyDigits(value);
  if (digits.length !== 14 || /^(\d)\1+$/.test(digits)) return false;
  const nums = digits.split("").map(Number);
  const d1 = weightedMod11(nums.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const d2 = weightedMod11(nums.slice(0, 13), [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  return nums[12] === d1 && nums[13] === d2;
}

export function isValidCpfCnpj(value) {
  const digits = onlyDigits(value);
  if (digits.length === 11) return isValidCpf(digits);
  if (digits.length === 14) return isValidCnpj(digits);
  return false;
}

function normalizePixPhone(value) {
  let digits = onlyDigits(value);
  if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) {
    digits = digits.slice(2);
  }
  if (digits.length < 10 || digits.length > 11) return "";
  return `+55${digits}`;
}

export function normalizePixKey(keyType, pixKey) {
  const type = String(keyType || "").trim().toLowerCase();
  const raw = String(pixKey || "").trim();
  switch (type) {
    case "cpf":
      return isValidCpf(raw) ? onlyDigits(raw) : "";
    case "cnpj":
      return isValidCnpj(raw) ? onlyDigits(raw) : "";
    case "phone":
      return normalizePixPhone(raw);
    case "email":
      return PIX_EMAIL.test(raw) ? raw.toLowerCase() : "";
    case "random":
      return raw.length === 36 && PIX_UUID.test(raw) ? raw.toLowerCase() : "";
    default:
      return "";
  }
}

export function validatePixRefund(refund) {
  const source = refund && typeof refund === "object" ? refund : {};
  const holder = String(source.holder || "").trim();
  const keyType = String(source.keyType || "").trim().toLowerCase();
  if (!holder) {
    return { ok: false, message: "Informe o titular." };
  }
  if (!isValidCpfCnpj(source.cpf)) {
    return { ok: false, message: "Informe um CPF ou CNPJ válido." };
  }
  if (!PIX_KEY_TYPES.includes(keyType)) {
    return { ok: false, message: "Selecione o tipo de chave PIX." };
  }
  const pixKey = normalizePixKey(keyType, source.pixKey);
  if (!pixKey) {
    return { ok: false, message: "Informe uma chave PIX válida para o tipo selecionado." };
  }
  return {
    ok: true,
    refund: {
      holder,
      paymentType: "pix",
      keyType,
      pixKey,
      cpf: onlyDigits(source.cpf),
    },
  };
}
